import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useSnackbar } from "notistack";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import EntityTable, { RowActionMenu } from "components/EntityTable";
import ConfirmDialog from "components/ConfirmDialog";
import SidePanel from "components/SidePanel";
import TagForm from "components/content/TagForm";
import usePaginatedResource from "hooks/usePaginatedResource";
import useDebounce from "hooks/useDebounce";
import { listTagsPaged, deleteTag } from "api/endpoints/tags";

// Website-scoped content tags management screen.
function TagsListPage() {
  const { websiteId } = useOutletContext();
  const { enqueueSnackbar } = useSnackbar();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const [panel, setPanel] = useState({ open: false, tag: null });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTags = useCallback(
    (pageParams) => {
      if (!websiteId) return { items: [], total: 0 };
      return listTagsPaged(websiteId, { ...pageParams, search: debouncedSearch });
    },
    [websiteId, debouncedSearch]
  );

  const { rows, total, loading, page, pageSize, setPage, setPageSize, refresh } =
    usePaginatedResource(fetchTags);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, setPage]);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteTag(websiteId, confirmDeleteId);
      enqueueSnackbar("Tag deleted", { variant: "success" });
      refresh();
    } catch {
      enqueueSnackbar("Failed to delete tag", { variant: "error" });
    } finally {
      setDeleting(false);
      setConfirmDeleteId(null);
    }
  };

  const openCreate = () => setPanel({ open: true, tag: null });
  const openEdit = (tag) => setPanel({ open: true, tag });
  const closePanel = () => setPanel({ open: false, tag: null });

  const columns = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 180 },
    {
      field: "slug",
      headerName: "Slug",
      flex: 1,
      minWidth: 180,
      renderCell: ({ value }) => value || "—",
    },
    {
      field: "actions",
      headerName: "",
      width: 64,
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <RowActionMenu
          actions={[
            { label: "Edit", icon: "edit", onClick: () => openEdit(row) },
            {
              label: "Delete",
              icon: "delete",
              color: "error",
              onClick: () => setConfirmDeleteId(row.content_tag_id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <MDBox>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <MDTypography variant="h5" fontWeight="bold">
          Tags
        </MDTypography>
        <MDButton variant="gradient" color="info" startIcon={<Icon>add</Icon>} onClick={openCreate}>
          New Tag
        </MDButton>
      </MDBox>

      <Card>
        <MDBox p={3}>
          <MDBox mb={2} maxWidth={320}>
            <MDInput
              fullWidth
              placeholder="Search by name or slug"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{ endAdornment: <Icon fontSize="small">search</Icon> }}
            />
          </MDBox>
          <EntityTable
            rows={rows}
            columns={columns}
            getRowId={(row) => row.content_tag_id}
            loading={loading}
            paginationMode="server"
            rowCount={total}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onRowClick={({ row }) => openEdit(row)}
          />
        </MDBox>
      </Card>

      <SidePanel
        open={panel.open}
        onClose={closePanel}
        title={panel.tag ? "Edit Tag" : "New Tag"}
        subtitle="Manage the tags used to label this website's content"
        width={760}
      >
        {panel.open && (
          <TagForm
            websiteId={websiteId}
            tag={panel.tag}
            onSaved={() => {
              closePanel();
              refresh();
            }}
          />
        )}
      </SidePanel>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title="Delete Tag"
        message="Are you sure you want to delete this tag?"
        confirmText="Delete"
        loading={deleting}
        onConfirm={confirmDelete}
        onClose={() => setConfirmDeleteId(null)}
      />
    </MDBox>
  );
}

export default TagsListPage;
